import { ofetch } from "ofetch";
import { env } from "@/env";

type UploadPart = {
  PartNumber: number;
  signedUrl: string;
};

type UploadedPart = {
  PartNumber: number;
  ETag: string;
};

type OnProgressObject = {
  sent: number;
  total: number;
  percentage: number;
};

// original source: https://github.com/pilovm/multithreaded-uploader/blob/master/frontend/uploader.js
export class Uploader {
  useTransferAcceleration: boolean;
  chunkSize: number;
  threadsQuantity: number;
  timeout: number;
  file: File;
  fileName: string;
  aborted: boolean;
  uploadedSize: number;
  progressCache: Record<number, number>;
  activeConnections: Record<number, XMLHttpRequest>;
  parts: UploadPart[];
  uploadedParts: UploadedPart[];
  fileId: string | null;
  fileKey: string | null;
  signal?: AbortSignal;
  onCompleteFn: (progress: OnProgressObject) => void;
  onProgressFn: (progress: OnProgressObject) => void;
  onErrorFn: (error: Error) => void;

  constructor(options: {
    useTransferAcceleration?: boolean;
    chunkSize?: number;
    threadsQuantity?: number;
    file: File;
    fileName: string;
    signal?: AbortSignal;
  }) {
    this.useTransferAcceleration = options.useTransferAcceleration || false;
    // this must be bigger than or equal to 5MB,
    // otherwise AWS will respond with:
    // "Your proposed upload is smaller than the minimum allowed size"
    options.chunkSize = options.chunkSize || 0;
    this.chunkSize = Math.max(1024 * 1024 * options.chunkSize, 1024 * 1024 * 5);
    // number of parallel uploads
    options.threadsQuantity = options.threadsQuantity || 0;
    this.threadsQuantity = Math.min(options.threadsQuantity || 5, 15);
    // adjust the timeout value to activate exponential backoff retry strategy
    this.timeout = 0;
    this.file = options.file;
    this.fileName = options.fileName;
    this.aborted = false;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];
    this.fileId = null;
    this.fileKey = null;
    this.signal = options.signal;
    this.onCompleteFn = () => {};
    this.onProgressFn = () => {};
    this.onErrorFn = () => {};

    // External AbortController also kills active XHRs. Without this,
    // ofetch calls would reject but mid-flight chunk PUTs would keep
    // running until they naturally finished.
    this.signal?.addEventListener("abort", () => this.abort(), { once: true });
  }

  async start() {
    return this.initialize();
  }

  async initialize() {
    try {
      // initializing the multipart request
      const videoInitializationUploadInput = {
        fileName: this.file.name,
        Key: this.fileName,
        size: this.file.size,
        mimeType: this.file.type,
      };
      const initializeReponse = await ofetch<{
        fileId: string;
        fileKey: string;
        jobId: string;
      }>(`${env.NEXT_PUBLIC_API_URL}/initializeMultipartUpload`, {
        method: "POST",
        body: videoInitializationUploadInput,
        signal: this.signal,
      });

      this.fileId = initializeReponse.fileId;
      this.fileKey = initializeReponse.fileKey;

      // retrieving the pre-signed URLs
      const numberOfparts = Math.ceil(this.file.size / this.chunkSize);

      const AWSMultipartFileDataInput = {
        UploadId: this.fileId,
        Key: this.fileKey,
        nParts: numberOfparts,
      };

      const urlsResponse = await ofetch<{ parts: UploadPart[] }>(
        `${env.NEXT_PUBLIC_API_URL}/getMultipartUploadPresigned`,
        { method: "POST", body: AWSMultipartFileDataInput, signal: this.signal }
      );

      const newParts = urlsResponse.parts;
      this.parts.push(...newParts);

      this.sendNext();
      return initializeReponse;
    } catch (error) {
      await this.complete(error instanceof Error ? error : new Error(String(error)));
    }
  }

  sendNext(retry = 0) {
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      return;
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete();
      }

      return;
    }

    const part = this.parts.pop();
    if (this.file && part) {
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        this.sendNext();
      };

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          this.sendNext();
        })
        .catch((error) => {
          if (retry <= 6) {
            retry++;
            const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));
            //exponential backoff retry before giving up
            console.log(
              `Part#${part.PartNumber} failed to upload, backing off ${
                2 ** retry * 100
              } before retrying...`
            );
            wait(2 ** retry * 100).then(() => {
              this.parts.push(part);
              this.sendNext(retry);
            });
          } else {
            console.log(`Part#${part.PartNumber} failed to upload, giving up`);
            this.complete(error instanceof Error ? error : new Error(String(error)));
          }
        });
    }
  }

  async complete(error: Error | undefined = undefined) {
    if (error && !this.aborted) {
      this.onErrorFn(error);
      return;
    }

    if (error) {
      this.onErrorFn(error);
      return;
    }

    try {
      await this.sendCompleteRequest();
    } catch (error) {
      this.onErrorFn(error instanceof Error ? error : new Error(String(error)));
    }
  }

  async sendCompleteRequest() {
    if (this.fileId && this.fileKey) {
      const videoFinalizationMultiPartInput = {
        fileId: this.fileId,
        fileKey: this.fileKey,
        parts: this.uploadedParts,
      };

      await ofetch(`${env.NEXT_PUBLIC_API_URL}/finalizeMultipartUpload`, {
        method: "POST",
        body: videoFinalizationMultiPartInput,
        signal: this.signal,
      });
      this.onCompleteFn({
        sent: this.file.size,
        total: this.file.size,
        percentage: 100,
      });
    }
  }

  sendChunk(chunk: Blob, part: UploadPart, sendChunkStarted: () => void) {
    return new Promise<void>((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then((status) => {
          if (status !== 200) {
            reject(new Error("Failed chunk upload"));
            return;
          }

          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  handleProgress(part: number, event: ProgressEvent | Event) {
    if (this.file) {
      if (event.type === "progress" || event.type === "error" || event.type === "abort") {
        this.progressCache[part] =
          "loaded" in event ? event.loaded : (this.progressCache[part] ?? 0);
      }

      if (event.type === "uploaded") {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);

      this.onProgressFn({
        sent,
        total,
        percentage,
      });
    }
  }

  upload(file: Blob, part: UploadPart, sendChunkStarted: () => void) {
    // uploading each part with its pre-signed URL
    return new Promise((resolve, reject) => {
      const throwXHRError = (error: Error | Event, part: UploadPart, abortFx: () => void) => {
        delete this.activeConnections[part.PartNumber - 1];
        const errorObj = error instanceof Error ? error : new Error("XHR request failed");
        reject(errorObj);
        window.removeEventListener("offline", abortFx);
      };
      if (this.fileId && this.fileKey) {
        if (!window.navigator.onLine) reject(new Error("System is offline"));

        const xhr = (this.activeConnections[part.PartNumber - 1] = new XMLHttpRequest());
        xhr.timeout = this.timeout;
        sendChunkStarted();

        const progressListener = this.handleProgress.bind(this, part.PartNumber - 1);

        xhr.upload.addEventListener("progress", progressListener);

        xhr.addEventListener("error", progressListener);
        xhr.addEventListener("abort", progressListener);
        xhr.addEventListener("loadend", progressListener);

        xhr.open("PUT", part.signedUrl);
        const abortXHR = () => xhr.abort();
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const ETag = xhr.getResponseHeader("ETag");

            if (ETag) {
              const uploadedPart = {
                PartNumber: part.PartNumber,
                ETag: ETag.replaceAll('"', ""),
              };

              this.uploadedParts.push(uploadedPart);

              resolve(xhr.status);
              delete this.activeConnections[part.PartNumber - 1];
              window.removeEventListener("offline", abortXHR);
            }
          }
        };

        xhr.onerror = (error) => {
          throwXHRError(error, part, abortXHR);
        };
        xhr.ontimeout = (error) => {
          throwXHRError(error, part, abortXHR);
        };
        xhr.onabort = () => {
          throwXHRError(new Error("Upload canceled by user or system"), part, abortXHR);
        };
        window.addEventListener("offline", abortXHR);
        xhr.send(file);
      }
    });
  }

  onProgress(onProgress: (progress: OnProgressObject) => void) {
    this.onProgressFn = onProgress;
    return this;
  }
  onComplete(onComplete: (progress: OnProgressObject) => void) {
    this.onCompleteFn = onComplete;
    return this;
  }

  onError(onError: (error: Error) => void) {
    this.onErrorFn = onError;
    return this;
  }

  abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        this.activeConnections[id].abort();
      });

    this.aborted = true;
  }
}
